import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { isString } from 'lodash';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';

import { RemoveSiteButtonComponent } from '@widgets/remove-site-button/remove-site-button.component';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';

import { SitesQuery } from '@models/sites/sites.query';

import { ButtonComponent } from '@shared/components/button/button.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { OverlayService } from '@shared/services/overlay.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Maybe, Utils } from '@shared/utils/utils';

import { getSiteWorkflow } from '../../directives/site-workflow.directive';
import { SiteInformationEditViewComponent } from './components/site-information-edit-view/site-information-edit-view.component';
import { SiteInformationViewComponent } from './components/site-information-view/site-information-view.component';

@Component({
  imports: [
    VarDirective,
    SiteInformationViewComponent,
    SiteInformationEditViewComponent,
    SiteWorkflowDirective,
    RemoveSiteButtonComponent,
    ButtonComponent,
    TooltipDirective,
    AsyncPipe,
  ],
  selector: 'aux-site-info',
  templateUrl: './site-info.components.html',
})
export class SiteInfoComponent {
  private overlayService = inject(OverlayService);
  private sitesQuery = inject(SitesQuery);
  private vendorQuery = inject(OrganizationQuery);

  editMode$ = new BehaviorSubject(false);
  site$ = this.sitesQuery.selectActive().pipe(
    map(([site]) => ({
      ...site,
      address_2: this.getAddressLineWithCorrectComma(
        site?.address_line_1,
        site?.address_line_2,
        site?.address_line_3
      ),
      address_3: this.getAddressLineWithCorrectComma(site?.city, site?.state, site?.zip),
      country_name: Utils.getCountryName(site?.country || ''),
      investigator_name: this.vendorQuery.getEntity(site?.managed_by_id)?.name,
      primary_investigator_name: this.getPrimaryInvestigatorName(
        site?.investigator?.family_name,
        site?.investigator?.given_name
      ),
    }))
  );
  siteChanged = false;
  siteWorkflow = getSiteWorkflow();

  async canDeactivate(): Promise<boolean> {
    if (this.editMode$.value && this.siteChanged) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event?.data;
    }

    return true;
  }

  editMode = () => {
    this.editMode$.next(true);
  };

  private getAddressLineWithCorrectComma(...rest: Maybe<string>[]): string {
    return rest
      .filter((value) => isString(value) && !!value)
      .reduce(
        (accum, address, index) => (index === 0 ? address : `${accum}, ${address}`.trim()),
        ''
      ) as string;
  }

  getPrimaryInvestigatorName(familyName?: string, givenName?: string) {
    let fullName = '';

    if (givenName) {
      fullName += givenName;
    }

    if (familyName) {
      fullName += ` ${familyName}`;
    }

    return fullName.trim();
  }

  siteDataChanged(changed: boolean) {
    this.siteChanged = changed;
  }
}
